import type React from 'react';
import { useTranslation } from 'react-i18next';
import { formatGel } from '@/utils/money';
import { MetricRow, OverviewCard } from '../overviewCard';
import { formatSignedGel } from '../overviewMeta';
import { NET_WORKING_CAPITAL as META } from './netWorkingCapitalMeta';
import type { NetWorkingCapitalData } from './useNetWorkingCapital';

type NetWorkingCapitalViewProps = {
    data: NetWorkingCapitalData;
};

/**
 * Net working capital — render only.
 *
 * Both sides of the subtraction are printed above the answer, in that order,
 * because the figure people distrust is the one they cannot check. The
 * liability row is tinted red for what it is — money owed — not because
 * anything is wrong with it.
 */
export const NetWorkingCapitalView: React.FC<NetWorkingCapitalViewProps> = ({
    data,
}) => {
    const { t } = useTranslation('pages');

    return (
        <OverviewCard
            delta={
                // No chip rather than a zero one: a business in its first month
                // has not stood still, it has no month to be compared with.
                data.deltaTetri === null
                    ? null
                    : {
                          label: formatSignedGel(data.deltaTetri),
                          tone: data.deltaTetri < 0 ? 'danger' : 'success',
                      }
            }
            icon={META.icon}
            question={t(META.keys.question)}
            slot={META.slot}
            title={t(META.keys.title)}
            tone={data.tone}
            verdict={{
                label: t(META.keys.difference),
                tone: data.tone,
                value: formatGel(data.differenceTetri),
            }}
        >
            <MetricRow
                label={t(META.keys.assets)}
                slot={`${META.slot}-assets`}
                value={formatGel(data.assetsTetri)}
            />
            <MetricRow
                label={t(META.keys.liabilities)}
                slot={`${META.slot}-liabilities`}
                tone="danger"
                value={formatGel(data.liabilitiesTetri)}
            />
        </OverviewCard>
    );
};
